<script setup lang="ts">
/**
 * "Dialogue preferences" dialog. User-level settings that apply across every
 * matter and dialogue, persisted via dialoguePreferencesStore (localStorage).
 *
 * Exposes two settings as radio groups — matter selection behavior and
 * references-pill behavior. The Save button stays disabled until a draft
 * diverges from its persisted value; closing via the x while dirty routes
 * through the shared DiscardChangesDialog.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useDialoguePreferencesStore,
    type MatterSelectionBehavior,
    type ReferencesPillBehavior,
} from '../../stores/dialoguePreferencesStore';
import DiscardChangesDialog from '../matter-context/DiscardChangesDialog.vue';

const { t } = useI18n();
const emit = defineEmits<{ close: [] }>();

const prefsStore = useDialoguePreferencesStore();

// Local drafts, seeded from the persisted values. Save commits them; the
// dialog only writes through to the store on Save, never on each radio change.
const draftMatterSelection = ref<MatterSelectionBehavior>(prefsStore.matterSelectionBehavior);
const draftReferencesPill = ref<ReferencesPillBehavior>(prefsStore.referencesPillBehavior);

const showDiscardWarning = ref(false);

const isDirty = computed(
    () =>
        draftMatterSelection.value !== prefsStore.matterSelectionBehavior ||
        draftReferencesPill.value !== prefsStore.referencesPillBehavior,
);

const save = () => {
    if (!isDirty.value) return;
    prefsStore.setMatterSelectionBehavior(draftMatterSelection.value);
    prefsStore.setReferencesPillBehavior(draftReferencesPill.value);
    emit('close');
};

// The x icon: warn before discarding unsaved edits, close immediately when clean.
const requestClose = () => {
    if (isDirty.value) {
        showDiscardWarning.value = true;
        return;
    }
    emit('close');
};

const confirmDiscard = () => {
    showDiscardWarning.value = false;
    emit('close');
};

const cancelDiscard = () => {
    showDiscardWarning.value = false;
};
</script>

<template>
    <div class="dlg-prefs-overlay" @click.self="requestClose">
        <div class="dlg-prefs" role="dialog" aria-modal="true" aria-labelledby="dlg-prefs-title">
            <button
                type="button"
                class="dlg-prefs-close"
                :title="t('common.close')"
                :aria-label="t('common.close')"
                @click="requestClose"
            >
                <span class="material-icons">close</span>
            </button>

            <h2 id="dlg-prefs-title" class="dlg-prefs-title">{{ t('dialoguePreferences.title') }}</h2>
            <p class="dlg-prefs-subtitle">
                {{ t('dialoguePreferences.subtitle') }}
            </p>

            <div class="dlg-prefs-card" role="radiogroup" aria-labelledby="dlg-prefs-legend-matter">
                <span id="dlg-prefs-legend-matter" class="dlg-prefs-legend">{{
                    t('dialoguePreferences.matterLegend')
                }}</span>

                <label class="dlg-prefs-option">
                    <input
                        v-model="draftMatterSelection"
                        type="radio"
                        name="matter-selection-behavior"
                        value="new-chat"
                    />
                    <span class="dlg-prefs-option-label">{{
                        t('dialoguePreferences.matterNewChat')
                    }}</span>
                </label>

                <label class="dlg-prefs-option">
                    <input
                        v-model="draftMatterSelection"
                        type="radio"
                        name="matter-selection-behavior"
                        value="most-recent"
                    />
                    <span class="dlg-prefs-option-label">{{
                        t('dialoguePreferences.matterMostRecent')
                    }}</span>
                </label>
            </div>

            <div class="dlg-prefs-card" role="radiogroup" aria-labelledby="dlg-prefs-legend-refs">
                <span id="dlg-prefs-legend-refs" class="dlg-prefs-legend">{{
                    t('dialoguePreferences.referencesLegend')
                }}</span>

                <label class="dlg-prefs-option">
                    <input
                        v-model="draftReferencesPill"
                        type="radio"
                        name="references-pill-behavior"
                        value="references-table"
                    />
                    <span class="dlg-prefs-option-label">{{
                        t('dialoguePreferences.referencesTable')
                    }}</span>
                </label>

                <label class="dlg-prefs-option">
                    <input
                        v-model="draftReferencesPill"
                        type="radio"
                        name="references-pill-behavior"
                        value="record-viewer"
                    />
                    <span class="dlg-prefs-option-label">{{
                        t('dialoguePreferences.referencesRecordViewer')
                    }}</span>
                </label>
            </div>

            <div class="dlg-prefs-actions">
                <button type="button" class="dlg-prefs-save" :disabled="!isDirty" @click="save">
                    {{ t('dialoguePreferences.save') }}
                </button>
            </div>
        </div>

        <DiscardChangesDialog
            :visible="showDiscardWarning"
            :title="t('dialoguePreferences.discardTitle')"
            :body="t('dialoguePreferences.discardBody')"
            @discard="confirmDiscard"
            @cancel="cancelDiscard"
        />
    </div>
</template>

<style scoped>
.dlg-prefs-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: var(--z-popover);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.dlg-prefs {
    position: relative;
    background: var(--color-bg-white);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    padding: 24px;
    width: min(560px, 100%);
    display: flex;
    flex-direction: column;
}

.dlg-prefs-close {
    position: absolute;
    top: 14px;
    right: 14px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: var(--radius-sm);
}

.dlg-prefs-close:hover {
    color: var(--color-primary);
}

.dlg-prefs-title {
    margin: 0 32px 4px 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--color-text-primary);
}

.dlg-prefs-subtitle {
    margin: 0 0 20px;
    font-size: var(--text-base);
    color: var(--color-text-secondary);
    line-height: 1.5;
}

/* Single bordered, lightly-filled card wrapping the whole radio group. */
.dlg-prefs-card {
    margin: 0 0 24px;
    padding: 18px 20px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    background: var(--color-bg-light);
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dlg-prefs-legend {
    margin: 0 0 4px;
    font-size: var(--text-base);
    font-weight: 600;
    color: var(--color-text-primary);
}

.dlg-prefs-option {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
}

.dlg-prefs-option input[type='radio'] {
    width: 18px;
    height: 18px;
    accent-color: var(--color-primary);
    flex-shrink: 0;
    cursor: pointer;
}

.dlg-prefs-option-label {
    font-size: var(--text-base);
    color: var(--color-text-primary);
}

.dlg-prefs-actions {
    display: flex;
    justify-content: flex-end;
}

.dlg-prefs-save {
    border: none;
    border-radius: var(--radius-pill);
    padding: 9px 22px;
    font-size: var(--text-base);
    font-weight: 500;
    background: var(--color-secondary);
    color: var(--color-bg-white);
    cursor: pointer;
}

.dlg-prefs-save:hover {
    filter: brightness(1.1);
}

.dlg-prefs-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: none;
}
</style>
